import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import { CloseOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'

import { useSmallScreen } from '@common/utils'

import { LAYER_DATA } from './LayerSelector'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object} props.data
 * @param {string} props.layer
 * @param {Object} [props.sx]
 */
export default function MapLegend({ data, layer, sx }) {
  const isSmallScreen = useSmallScreen()
  const legendContainerRef = useRef()

  const [isOverflowing, setIsOverflowing] = useState(false)
  const [moreModalOpen, setMoreModalOpen] = useState(false)

  useLayoutEffect(() => {
    const el = legendContainerRef.current
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth)
    }
  }, [layer])

  const filteredLegend = useMemo(() => {
    if (!data?.table?.legend) return {}

    const result = {}
    Object.keys(data.table.legend).forEach((item) => {
      const entries = Object.entries(data.table.legend[item])
      const processedEntries = entries.filter(([, entry]) => entry.count)
      result[item] = Object.fromEntries(processedEntries)
    })
    return result
  }, [data])

  return (
    <>
      <Dialog
        fullWidth
        open={moreModalOpen}
        onClose={() => setMoreModalOpen(false)}
        maxWidth="xl"
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', pt: 3, pb: 0, px: 4 }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="h6">Connectivity Map Legend</Typography>
            <Typography variant="caption" color="grey">
              {LAYER_DATA[layer]?.label ?? 'Unknown Layer'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setMoreModalOpen(false)}
            sx={{ width: 52, height: 52 }}
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ mt: 2 }} />
        <DialogContent sx={{ px: 4 }}>
          <Grid container spacing={2}>
            {Object.entries(filteredLegend[layer]).map(([key, { color, count }]) => (
              <Grid key={key} size={isSmallScreen.mobile ? 6 : 2}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box width={10} height={10} sx={{ backgroundColor: color }} />
                  <Typography noWrap variant="caption" textOverflow="ellipsis">
                    {key} ({count})
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
      <Box display="flex" flexDirection="column" justifyContent="space-between" sx={sx}>
        <Typography variant="body">Legend</Typography>
        <Box
          display="flex"
          position="relative"
          sx={{
            border: 1,
            borderRadius: 1,
            borderColor: '#e0e0e0',
            px: 1.5,
            py: 0.5,
            mt: 1,
            minHeight: 40,
          }}
        >
          {isOverflowing && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                left: 0,
                top: 0,
                background: 'linear-gradient(90deg, transparent 80%, white 100%)',
              }}
            />
          )}
          <Box
            ref={legendContainerRef}
            display="flex"
            flexWrap="nowrap"
            alignItems="center"
            sx={{ gap: 1, overflow: 'hidden' }}
          >
            {Object.entries(filteredLegend[layer]).map(([key, { color, count }]) => (
              <Box key={key} display="flex" alignItems="center" gap={0.5}>
                <Box width={10} height={10} sx={{ backgroundColor: color }} />
                <Typography noWrap variant="caption" textOverflow="ellipsis">
                  {key} ({count})
                </Typography>
              </Box>
            ))}
          </Box>
          {isOverflowing && (
            <Button size="small" sx={{ ml: 1 }} onClick={() => setMoreModalOpen(true)}>
              More
            </Button>
          )}
        </Box>
      </Box>
    </>
  )
}
